import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpDown, BarChart3, X } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import XpnsrPageHeader from './XpnsrPageHeader';

const METRICS = [
  { key: 'clicks', label: 'Clicks', fmt: (v) => v.toLocaleString() },
  { key: 'unique_clicks', label: 'Unique', fmt: (v) => v.toLocaleString() },
  { key: 'conversions', label: 'Conv.', fmt: (v) => v.toLocaleString() },
  { key: 'revenue', label: 'Revenue', fmt: (v) => '$' + v.toFixed(2) },
  { key: 'cost', label: 'Cost', fmt: (v) => '$' + v.toFixed(2) },
  { key: 'cr', label: 'CR %', fmt: (v) => v.toFixed(2) + '%' },
  { key: 'epc', label: 'EPC', fmt: (v) => '$' + v.toFixed(4) },
  { key: 'roi', label: 'ROI %', fmt: (v) => v.toFixed(1) + '%' },
];

const COLORS = [
  'rgb(var(--primary))',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#f97316',
  '#14b8a6',
  '#6366f1',
];

export default function CampaignComparison() {
  const { t } = useT();
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/campaigns`)
      .then(r => setCampaigns(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error(t('Failed to load campaigns')));
  }, []);

  const compare = async () => {
    if (selected.length < 2) {
      toast.error(t('Select at least 2 campaigns'));
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/campaigns/compare`, { ids: selected });
      setResults(r.data || []);
    } catch {
      toast.error(t('Failed to compare'));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length >= 10 ? prev : [...prev, id]
    );
  };

  const chartData = results.map(r => ({ name: r.name?.slice(0, 20) || r.id, ...r }));

  return (
    <div style={{ padding: '0 32px' }}>
      <XpnsrPageHeader title={t('Campaign comparison')} subtitle={t('Side-by-side metrics for selected campaigns')} />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm">{t('Select campaigns (2–10)')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {selected.map(id => {
              const c = campaigns.find(x => x.id === id);
              return (
                <Badge key={id} variant="secondary" className="gap-1 cursor-pointer" onClick={() => toggle(id)}>
                  {c?.name || id.slice(0, 8)}
                  <X className="w-3 h-3" />
                </Badge>
              );
            })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
            {campaigns.filter(c => !c.is_archived).map(c => (
              <label
                key={c.id}
                className={`flex items-center gap-2 p-2 rounded border text-sm cursor-pointer transition-colors ${selected.includes(c.id) ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/5'}`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={() => toggle(c.id)}
                  className="rounded"
                />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
          </div>
          <div className="mt-4">
            <Button onClick={compare} disabled={selected.length < 2 || loading}>
              <ArrowUpDown className="w-4 h-4 mr-2" />
              {loading ? t('Comparing…') : t('Compare')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          {/* Comparison table */}
          <Card className="mb-6 overflow-x-auto">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-3 text-left font-medium text-muted-foreground">{t('Metric')}</th>
                    {results.map((r, i) => (
                      <th key={r.id} className="p-3 text-right font-medium" style={{ color: COLORS[i % COLORS.length] }}>
                        {r.name?.slice(0, 20) || r.id.slice(0, 8)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(m => {
                    const vals = results.map(r => r[m.key] || 0);
                    const best = Math.max(...vals);
                    return (
                      <tr key={m.key} className="border-b border-border/50 hover:bg-muted/5">
                        <td className="p-3 font-medium">{t(m.label)}</td>
                        {results.map((r, i) => {
                          const v = r[m.key] || 0;
                          const isBest = v === best && best > 0;
                          return (
                            <td key={r.id} className={`p-3 text-right tabular-nums ${isBest ? 'font-semibold text-primary' : ''}`}>
                              {m.fmt(v)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Revenue chart */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> {t('Revenue comparison')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="rgb(var(--primary))" name={t('Revenue')} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cost" fill="#f59e0b" name={t('Cost')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* CR chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> {t('Conversion rate comparison')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip />
                  <Bar dataKey="cr" fill="#10b981" name={t('CR %')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
